import { useTeam } from 'app/frontend/base/config/context/team'
import { useMemo } from 'react'
import { useTile } from '../../../context'
import { useOverviewData, usePageInfo } from 'app/frontend/base/config/queries'
import {
  age,
  characterName,
  role,
} from 'app/frontend/base/config/column-definitions/application'
import {
  itemLevel,
  mythicRating,
  raidParse,
  raidProgress,
} from 'app/frontend/base/config/column-definitions/character'
import { useCharacterDetailsStore } from 'app/frontend/base/state/useCharacterDetailsStore'
import { DataTable } from '@/components/ui/data-table'
import { CircleCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'

const TableView = () => {
  const { team } = useTeam()
  const { tile } = useTile()
  const navigate = useNavigate()
  const { defaultDifficulty } = useDifficulties()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { data: overviewData, isPending } = useOverviewData(team.id)
  const applications = (overviewData?.pendingApplications || []).slice(
    0,
    tile.config?.amount ?? 5,
  )

  const { detailsStore, immediatelyFetchNewDetails } =
    useCharacterDetailsStore<wowaudit.Application>(
      (application) => application.characterReference?.id,
    )

  const columns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      role({ withFilters: false }),
      characterName({ withFilters: false }),
      itemLevel<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidProgress<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      raidParse<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      ...(gameVersion === 'live'
        ? [
            mythicRating<wowaudit.Application>(
              detailsStore,
              (application) => application.characterReference?.id,
            ),
          ]
        : []),
      age,
    ],
    [detailsStore, defaultDifficulty, gameVersion],
  )

  const handleRowClick = (application: wowaudit.Application) => {
    navigate(`${team.path}/applicants/${application.id}`)
  }

  return (
    <DataTable
      data={applications ?? []}
      columns={columns}
      onVisibleRowsChange={immediatelyFetchNewDetails}
      includeHeader={true}
      emptyMessage={() => (
        <div
          className={`flex flex-col items-center gap-4 my-8 ${
            isPending ? 'opacity-0' : ''
          }`}
        >
          <CircleCheck size={48} className='text-success-foreground' />{' '}
          <span>There are no pending applications</span>
        </div>
      )}
      sortingState={[{ id: 'age', desc: false }]}
      getRowId={(application) => application.id.toString()}
      onRowClick={handleRowClick}
      rowClassName={() => `hover:bg-card-nested`}
    />
  )
}

export default TableView
